// Sprint 17 A3: Deal Intelligence Panel — momentum, coaching, stall, similar deals
import { useId } from "react";
import {
  Flame, Clock, Snowflake, Brain, AlertTriangle,
  TrendingUp, TrendingDown, Minus, Trophy, XCircle,
  Lightbulb, BookOpen, History,
} from "lucide-react";
import { Skeleton, SkeletonCard } from "@/components/skeleton";
import { useDealIntelligence, useDealTimeMachine } from "@/hooks/use-crm-deal-intelligence";
import { formatCurrency, formatDate } from "@/lib/i18n";
import type { DealIntelligence, CoachingTip, SimilarDeal } from "@/hooks/use-crm-deal-intelligence";

interface DealIntelligencePanelProps {
  dealId: string;
}

const MOMENTUM_CONFIG = {
  accelerating: { Icon: Flame, color: "text-[var(--color-accent-action)]", bg: "bg-[var(--color-accent-action-light)]", label: "Hot" },
  stable: { Icon: Clock, color: "text-[var(--color-warning)]", bg: "bg-[var(--color-warning)]/10", label: "Stable" },
  decelerating: { Icon: Snowflake, color: "text-[var(--color-info)]", bg: "bg-[var(--color-info)]/10", label: "Cold" },
} as const;

export function DealIntelligencePanel({ dealId }: DealIntelligencePanelProps) {
  const { data, isLoading } = useDealIntelligence(dealId);
  const { data: timeMachine } = useDealTimeMachine(dealId);

  if (isLoading) return <IntelligenceSkeleton />;
  if (!data) return null;

  return (
    <div className="space-y-4">
      <h3 className="text-xs font-semibold text-[var(--color-muted-foreground)] uppercase tracking-wide flex items-center gap-1.5">
        <Brain className="h-3.5 w-3.5 text-[var(--color-ai)]" />
        Deal Intelligence
      </h3>

      {/* Momentum Gauge */}
      <MomentumSection momentum={data.momentum} />

      {/* Probability */}
      <ProbabilityBar probability={data.probability} expectedRevenue={data.expectedRevenue} />

      {/* Stall Warning */}
      {data.stallWarning.isStalled && (
        <StallWarningBanner warning={data.stallWarning} />
      )}

      {/* Coaching Tips */}
      {data.coaching.length > 0 && (
        <CoachingSection tips={data.coaching} />
      )}

      {data.playbook && (
        <PlaybookSection playbook={data.playbook} />
      )}

      {/* Similar Deals */}
      {data.similarDeals.length > 0 && (
        <SimilarDealsSection deals={data.similarDeals} />
      )}

      {(timeMachine ?? data.timeMachine) && (
        <TimeMachineSection timeMachine={(timeMachine ?? data.timeMachine)!} />
      )}
    </div>
  );
}

function MomentumSection({ momentum }: { momentum: DealIntelligence["momentum"] }) {
  const config = MOMENTUM_CONFIG[momentum.direction];
  const MomentumIcon = config.Icon;

  return (
    <div className={`rounded-lg border border-[var(--color-border)] p-3 ${config.bg}`}>
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <MomentumIcon className={`h-4 w-4 ${config.color}`} />
          <span className={`text-sm font-medium ${config.color}`}>{config.label} Momentum</span>
        </div>
        <span className="text-sm font-bold text-[var(--color-foreground)]">{momentum.score}</span>
      </div>

      {/* Mini factor bars */}
      <div className="space-y-1.5">
        {momentum.factors.map((f) => {
          const barColor = f.impact === "positive" ? "bg-[var(--color-success)]"
            : f.impact === "negative" ? "bg-[var(--color-destructive)]"
            : "bg-[var(--color-muted-foreground)]";
          const ImpactIcon = f.impact === "positive" ? TrendingUp
            : f.impact === "negative" ? TrendingDown : Minus;

          return (
            <div key={f.factor} className="stagger-item flex items-center gap-2 text-xs">
              <ImpactIcon className={`h-3 w-3 flex-shrink-0 ${
                f.impact === "positive" ? "text-[var(--color-success)]"
                : f.impact === "negative" ? "text-[var(--color-destructive)]"
                : "text-[var(--color-muted-foreground)]"
              }`} />
              <span className="text-[var(--color-muted-foreground)] w-20 truncate">{f.description}</span>
              <div className="flex-1 h-1.5 bg-[var(--color-muted)] rounded-full overflow-hidden">
                <div className={`h-full rounded-full ${barColor}`} style={{ width: `${f.weight * 100}%` }} />
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function ProbabilityBar({ probability, expectedRevenue }: { probability: number; expectedRevenue: number }) {
  const gradientId = useId();

  return (
    <div className="rounded-lg border border-[var(--color-border)] p-3 bg-[var(--color-card)]">
      <div className="flex items-center justify-between mb-2">
        <span className="text-xs text-[var(--color-muted-foreground)]">Win Probability</span>
        <span className="text-sm font-bold text-[var(--color-foreground)]">{probability}%</span>
      </div>
      <div className="h-2 bg-[var(--color-muted)] rounded-full overflow-hidden">
        <svg width="100%" height="100%" className="rounded-full" aria-hidden="true">
          <defs>
            <linearGradient id={gradientId}>
              <stop offset="0%" stopColor="var(--color-primary)" />
              <stop offset="100%" stopColor="var(--color-ai)" />
            </linearGradient>
          </defs>
          <rect width={`${probability}%`} height="100%" fill={`url(#${gradientId})`} rx="4" />
        </svg>
      </div>
      <p className="text-xs text-[var(--color-muted-foreground)] mt-1">
        Expected: <span className="font-medium text-[var(--color-foreground)]">
          {formatCurrency(expectedRevenue, "EUR", { maximumFractionDigits: 0 })}
        </span>
      </p>
    </div>
  );
}

function StallWarningBanner({ warning }: { warning: DealIntelligence["stallWarning"] }) {
  return (
    <div className="rounded-lg border border-[var(--color-destructive)]/30 bg-[var(--color-destructive)]/5 p-3">
      <div className="flex items-start gap-2">
        <AlertTriangle className="h-4 w-4 text-[var(--color-destructive)] mt-0.5 flex-shrink-0" />
        <div>
          <p className="text-sm font-medium text-[var(--color-destructive)]">
            Deal Stalling — {warning.daysSinceActivity}d without activity
          </p>
          <p className="text-xs text-[var(--color-muted-foreground)] mt-0.5">
            Average for this stage: {warning.averageDaysInStage}d
          </p>
          {warning.recommendedAction && (
            <p className="text-xs text-[var(--color-foreground)] mt-1 font-medium">
              <Lightbulb className="h-3 w-3 inline mr-0.5 text-[var(--color-warning)]" />
              {warning.recommendedAction}
            </p>
          )}
        </div>
      </div>
    </div>
  );
}

function CoachingSection({ tips }: { tips: CoachingTip[] }) {
  return (
    <div>
      <h4 className="text-xs font-semibold text-[var(--color-ai)] mb-2 flex items-center gap-1">
        <Brain className="h-3 w-3" /> AI Coaching
      </h4>
      <div className="space-y-2">
        {tips.map((tip) => (
          <div key={tip.id} className="stagger-item rounded-lg border border-[var(--color-ai-border)] bg-[var(--color-ai-light)] p-3">
            <div className="flex items-center gap-2 mb-1">
              <span className="text-xs font-medium text-[var(--color-ai)] capitalize">{tip.category}</span>
              {tip.aiGenerated && (
                <span className="text-[9px] px-1 py-0.5 rounded bg-[var(--color-ai)]/10 text-[var(--color-ai)] border border-[var(--color-ai-border)]">
                  AI {Math.round(tip.confidence * 100)}%
                </span>
              )}
            </div>
            <p className="text-xs text-[var(--color-foreground)]">{tip.suggestion}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

function SimilarDealsSection({ deals }: { deals: SimilarDeal[] }) {
  return (
    <div>
      <h4 className="text-xs font-semibold text-[var(--color-muted-foreground)] mb-2">Similar Deals</h4>
      <div className="space-y-2">
        {deals.map((deal) => {
          const OutcomeIcon = deal.outcome === "won" ? Trophy : XCircle;
          const outcomeColor = deal.outcome === "won" ? "text-[var(--color-success)]" : "text-[var(--color-destructive)]";
          return (
            <div key={deal.dealId} className="stagger-item rounded-lg border border-[var(--color-border)] bg-[var(--color-card)] p-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <OutcomeIcon className={`h-3.5 w-3.5 ${outcomeColor}`} />
                  <span className="text-xs font-medium text-[var(--color-foreground)]">{deal.title}</span>
                </div>
                <span className="text-[10px] text-[var(--color-muted-foreground)]">
                  {Math.round(deal.similarity * 100)}% match
                </span>
              </div>
              {deal.lessonsLearned && (
                <p className="text-[10px] text-[var(--color-muted-foreground)] mt-1 pl-5">
                  {deal.lessonsLearned}
                </p>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function PlaybookSection({ playbook }: { playbook: NonNullable<DealIntelligence["playbook"]> }) {
  return (
    <div className="rounded-lg border border-[var(--color-border)] bg-[var(--color-card)] p-3">
      <h4 className="mb-2 flex items-center gap-1 text-xs font-semibold text-[var(--color-foreground)]">
        <BookOpen className="h-3.5 w-3.5 text-[var(--color-primary)]" />
        {playbook.title}
      </h4>
      <ol className="space-y-2">
        {playbook.steps.map((step, index) => (
          <li key={`${playbook.title}-${step}`} className="stagger-item flex gap-2 text-xs text-[var(--color-muted-foreground)]">
            <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-[var(--color-primary-light)] text-[10px] font-semibold text-[var(--color-primary)]">
              {index + 1}
            </span>
            <span>{step}</span>
          </li>
        ))}
      </ol>
    </div>
  );
}

function TimeMachineSection({ timeMachine }: { timeMachine: NonNullable<DealIntelligence["timeMachine"]> }) {
  const events = timeMachine.events ?? timeMachine.stageHistory.map((transition) => ({
    id: transition.id,
    title: `${transition.fromStage} -> ${transition.toStage}`,
    description: transition.reason,
    timestamp: transition.transitionedAt,
  }));

  return (
    <div className="rounded-lg border border-[var(--color-border)] bg-[var(--color-card)] p-3">
      <h4 className="mb-2 flex items-center gap-1 text-xs font-semibold text-[var(--color-muted-foreground)]">
        <History className="h-3.5 w-3.5" />
        Deal time machine
      </h4>
      <p className="mb-3 text-[10px] text-[var(--color-muted-foreground)]">
        Current stage: {timeMachine.currentStage} for {timeMachine.daysInStage}d
      </p>
      {events.length === 0 ? (
        <p className="text-xs text-[var(--color-muted-foreground)]">No historical deal events yet.</p>
      ) : (
        <div className="space-y-2">
          {events.slice(0, 5).map((event) => (
            <div key={event.id} className="stagger-item rounded-md border border-[var(--color-border)] bg-[var(--color-background)] px-2 py-2">
              <div className="flex items-center justify-between gap-2">
                <p className="text-xs font-medium text-[var(--color-foreground)]">{event.title}</p>
                <span className="text-[10px] text-[var(--color-muted-foreground)]">{formatDate(event.timestamp)}</span>
              </div>
              {event.description && (
                <p className="mt-1 text-[10px] text-[var(--color-muted-foreground)]">{event.description}</p>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function IntelligenceSkeleton() {
  return (
    <div className="space-y-4">
      <Skeleton className="h-3 w-28" />
      <SkeletonCard className="space-y-2" />
      <SkeletonCard className="space-y-2" />
    </div>
  );
}
